import React, { useState } from "react";
import { FormControl, FormLabel, Stack, TextField, Typography } from "@mui/material";

interface DurationHmsInputProps {
  label: string;
  valueSeconds: number;
  onChange: (seconds: number) => void;
  disabled?: boolean;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function parsePart(value: string, fallback: number): number {
  const trimmed = value.trim();
  if (trimmed === "") return fallback;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : fallback;
}

interface PartFieldProps {
  value: number;
  max?: number;
  pad?: boolean;
  ariaLabel: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}

function PartField({ value, max, pad = false, ariaLabel, onChange, disabled }: PartFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);

  const displayValue = draft ?? (pad ? value.toString().padStart(2, "0") : String(value));

  const normalize = (raw: string) => {
    let next = parsePart(raw, value);
    if (max !== undefined) next = clamp(next, 0, max);
    else next = Math.max(0, next);
    return next;
  };

  const commit = (raw: string) => {
    onChange(normalize(raw));
    setDraft(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const delta = e.key === "ArrowUp" ? 1 : -1;
    const next = max !== undefined ? clamp(value + delta, 0, max) : Math.max(0, value + delta);
    onChange(next);
    setDraft(null);
  };

  return (
    <TextField
      size="small"
      disabled={disabled}
      value={displayValue}
      onChange={(e) => {
        const v = e.target.value.replace(/\D/g, "");
        setDraft(v);
        if (v !== "") {
          onChange(normalize(v));
        }
      }}
      onBlur={() => commit(draft ?? displayValue)}
      onFocus={(e) => {
        setDraft(pad ? value.toString().padStart(2, "0") : String(value));
        e.target.select();
      }}
      onKeyDown={handleKeyDown}
      inputProps={{
        inputMode: "numeric",
        "aria-label": ariaLabel,
        maxLength: max !== undefined ? 2 : 3,
        style: { textAlign: "center" },
      }}
      sx={{ width: pad ? 52 : 44 }}
    />
  );
}

export function DurationHmsInput({ label, valueSeconds, onChange, disabled }: DurationHmsInputProps) {
  const hours = Math.floor(valueSeconds / 3600);
  const minutes = Math.floor((valueSeconds % 3600) / 60);
  const seconds = Math.floor(valueSeconds % 60);

  const emit = (h: number, m: number, s: number) => {
    onChange(Math.max(0, h * 3600 + m * 60 + s));
  };

  return (
    <FormControl fullWidth disabled={disabled}>
      <FormLabel sx={{ mb: 0.5, fontSize: "0.875rem" }}>{label}</FormLabel>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <PartField
          value={hours}
          ariaLabel={`${label} hours`}
          onChange={(h) => emit(h, minutes, seconds)}
          disabled={disabled}
        />
        <Typography color="text.secondary" sx={{ userSelect: "none" }}>
          :
        </Typography>
        <PartField
          value={minutes}
          max={59}
          pad
          ariaLabel={`${label} minutes`}
          onChange={(m) => emit(hours, m, seconds)}
          disabled={disabled}
        />
        <Typography color="text.secondary" sx={{ userSelect: "none" }}>
          :
        </Typography>
        <PartField
          value={seconds}
          max={59}
          pad
          ariaLabel={`${label} seconds`}
          onChange={(s) => emit(hours, minutes, s)}
          disabled={disabled}
        />
      </Stack>
    </FormControl>
  );
}
